import { createRouter, createWebHistory } from "vue-router";
import { useUserStore } from "@/stores/user";
import { useRequestsStore } from "@/stores/requests";
import { externalPartnerRedirectPaths, redirectPaths } from "./redirects";
import ForgotPasswordPage from "@/pages/authentication/ForgotPasswordPage.vue";
import MfaEnrollmentPage from "@/pages/authentication/MfaEnrollmentPage.vue";
import ResetPasswordPage from "@/pages/authentication/ResetPasswordPage.vue";
import SignInPage from "@/pages/authentication/SignInPage.vue";
import SignUpPage from "@/pages/authentication/SignUpPage.vue";
import UnlockUserPage from "@/pages/authentication/UnlockUserPage.vue";
import DashboardPage from "@/pages/dashboard/DashboardPage.vue";
import NotFoundPage from "@/pages/error/NotFoundPage.vue";
import CategoryPage from "@/pages/requests/CategoryPage.vue";
import RequestPage from "@/pages/requests/RequestPage.vue";
import RequestsPage from "@/pages/requests/RequestsPage.vue";
import AboutUsPage from "@/pages/welcome/AboutUs.vue";
import AboutYouPage from "@/pages/welcome/AboutYou.vue";
import ContactInfoPage from "@/pages/welcome/ContactInfo.vue";
import { getExpiringUrl } from "@/services/FileUploadService";
import AccountPage from "@/pages/account/AccountPage.vue";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else if (to.hash) {
      return {
        el: to.hash,
      };
    } else {
      return { top: 0 };
    }
  },
  routes: [
    ...externalPartnerRedirectPaths,
    ...redirectPaths,
    {
      path: "/account",
      name: "Account",
      component: AccountPage,
      meta: { requiresAuth: true },
    },
    {
      path: "/attachments/:fileKey",
      name: "Attachment",
      beforeEnter: async (to) => {
        const fileKey = decodeURIComponent(to.params.fileKey);
        if (!fileKey) {
          return { name: "NotFound" };
        }
        const url = await getExpiringUrl(fileKey);
        window.location = url;
        return false;
      },
    },
    {
      path: "/welcome",
      redirect: { name: "AboutUs" },
    },
    {
      path: "/welcome/about-us",
      name: "AboutUs",
      component: AboutUsPage,
      meta: { requiresAuth: true },
    },
    {
      path: "/welcome/about-you",
      name: "AboutYou",
      component: AboutYouPage,
      meta: { requiresAuth: true },
    },
    {
      path: "/welcome/contact-info",
      name: "ContactInfo",
      component: ContactInfoPage,
      meta: { requiresAuth: true },
    },
    {
      path: "/sign-in",
      name: "SignIn",
      component: SignInPage,
    },
    {
      path: "/sign-up/:companySlug?",
      name: "SignUp",
      component: SignUpPage,
    },
    {
      path: "/forgot-password",
      name: "ForgotPassword",
      component: ForgotPasswordPage,
    },
    {
      path: "/reset-password/:resetToken",
      name: "ResetPassword",
      component: ResetPasswordPage,
    },
    {
      path: "/mfa-enrollment",
      name: "MfaEnrollment",
      component: MfaEnrollmentPage,
      beforeEnter: (_to, _from, next) => {
        const userStore = useUserStore();
        if (
          !userStore.isAuthenticated ||
          (!userStore.mfaEnrollmentAvailable && !userStore.mfaVerificationRequired)
        ) {
          next({ name: "SignIn" });
        } else {
          next();
        }
      },
    },
    {
      path: "/:unlockToken/unlock",
      redirect: (to) => {
        return { path: `/unlock/${to.params.unlockToken}` };
      },
    },
    {
      path: "/reset-password/:unlockToken/unlock:userId(\\.?\\d+)?",
      redirect: (to) => {
        return { path: `/unlock/${to.params.unlockToken}` };
      },
    },
    {
      path: "/unlock/:unlockToken",
      name: "UnlockUser",
      component: UnlockUserPage,
    },
    {
      path: "/",
      redirect: () => {
        const userStore = useUserStore();
        return userStore.isAuthenticated ? { name: "Dashboard" } : { name: "SignIn" };
      },
    },
    {
      path: "/dashboard",
      name: "Dashboard",
      component: DashboardPage,
      meta: { requiresAuth: true },
      beforeEnter: (_to, _from, next) => {
        const requestsStore = useRequestsStore();
        requestsStore.refreshOpenRequests();
        requestsStore.refreshInProgressRequests();
        requestsStore.fetchSuggestedRequests();
        next();
      },
    },
    {
      path: "/requests",
      name: "Requests",
      component: RequestsPage,
      meta: { requiresAuth: true },
      beforeEnter: (_to, _from, next) => {
        const requestsStore = useRequestsStore();
        requestsStore.refreshAllRequests();
        next();
      },
    },
    {
      path: "/requests/:requestId",
      name: "Request",
      component: RequestPage,
      meta: { requiresAuth: true },
      beforeEnter: (to, _from, next) => {
        const requestsStore = useRequestsStore();
        requestsStore.fetchRequest(to.params.requestId);
        next();
      },
    },
    {
      path: "/requests/category/:categoryId",
      name: "RequestCategory",
      component: CategoryPage,
      meta: { requiresAuth: true },
    },
    {
      path: "/:pathMatch(.*)*",
      name: "NotFound",
      component: NotFoundPage,
    },
  ],
});

function requiresAuthentication(to) {
  return to.matched?.some((route) => route.meta?.requiresAuth);
}

function shouldRedirectToSignIn(to) {
  const userStore = useUserStore();
  if (!userStore.isAuthenticated && requiresAuthentication(to) && to.name !== "SignIn") {
    return true;
  }
  return false;
}

router.beforeEach(async (to) => {
  const userStore = useUserStore();
  if (shouldRedirectToSignIn(to)) {
    if (to.redirectedFrom?.path !== "/") {
      sessionStorage.setItem("returnTo", to.fullPath);
    }
    return { name: "SignIn" };
  } else if (
    userStore.isAuthenticated &&
    (to.name === "SignUp" ||
      (to.name === "SignIn" &&
        !userStore.mfaVerificationRequired &&
        !userStore.mfaEnrollmentAvailable))
  ) {
    return { name: "Dashboard" };
  }
});

// Lazy-loaded routes will get new hashes after a deploy and cached clients will try
// to load the old hash, causing the navigation to fail. This triggers a full reload
// of the page that the user is attempting to navigate to if the lazy-loaded route fails.
router.onError((error, to) => {
  if (
    error.message.includes("Failed to fetch dynamically imported module") ||
    error.message.includes("Importing a module script failed") ||
    error.message.includes("error loading dynamically imported module")
  ) {
    window.location.href = to.fullPath;
  }
});

export default router;
